import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Box,
    Paper,
    Button,
    TextField,
    Typography,
    Alert,
    IconButton,
    Autocomplete,
    Select,
    MenuItem
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Progress from "../../../components/progress/Progress";
import "../../../assets/css/time.css";
import BuildingService from "../../../services/building/BuildingService";
import IEditBuildingRequest from "../../../models/requests/building/editBuildingRequest";
import ISchedule from "../../../models/Schedule";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
    FormWrapper,
    EntryWrapper,
    EntryTitle,
    EntryField,
} from "../../../components/customStyles/form";
import IFloor from "../../../models/floor";
import FloorService from "../../../services/floor/FloorService";
import DataTable, { IColumn } from "../../../components/table/data/DataTable";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import NoMapIcon from "@mui/icons-material/DoDisturbAltOutlined";
import {
    convertScheduleToTicks,
    defaultMinTime,
    defaultMaxTime,
} from "./BuildingAddPage";
import localService from "../../../services/local/LocalService";
import ILocal from "../../../models/local";
import profileService from "../../../services/profile/ProfileService";
import ITimeZoneInfo from "../../../models/timezone";
import { IPropertyGroup } from "../../../models/propertyGroup";
import propertyGroupService from "../../../services/propertyGroupService/PropertyGroupService";
import IAdminPageBuildingDetail, { initialBuildingDetail } from "../../../models/adminPage/adminPageBuildingDetail";
import EditOnly from "../../../components/permissions/EditOnly";

const useFloorColumns = () => {
    const { t } = useTranslation();

    const columns: IColumn<IFloor>[] = [
        { field: "name", headerName: t("table.floorName") },
        { field: "floorNumber", headerName: t("table.floorNumber") },
        {
            field: "hbscanMapUrl",
            headerName: t("table.hbscanMap"),
            transform: {
                render: (data) => (
                    <Typography>
                        {data.hbscanMapUrl ? (
                            <MapOutlinedIcon></MapOutlinedIcon>
                        ) : (
                            <NoMapIcon></NoMapIcon>
                        )}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.hbscanMapUrl ? "✓" : "☓";
                },
            },
        },
    ];
    return columns;
};

const useLocalColumns = () => {
    const { t } = useTranslation();

    const columns: IColumn<ILocalWithFloorName>[] = [
        {
            field: "name",
            headerName: t("table.local.name"),
        },
        {
            field: "profileName",
            headerName: t("local.profile"),
        },
        {
            field: "floorName",
            headerName: t("table.local.floor"),
        },
        {
            field: "codes",
            headerName: t("local.codes"),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.codes.join(", ")}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.codes.join(", ");
                },
            },
        },
    ];
    return columns;
};

interface ILocalWithFloorName extends ILocal {
    floorName?: string;
}

const initialBuildingState: IEditBuildingRequest = {
    id: 0,
    name: "",
    address: "",
    timezone: "",
    propertyGroupId: 0
};

const ROWS_PER_PAGE = 10;
const SUCCESS_STATUS_CODE = 200;

const BuildingEditPage = () => {
    const { t } = useTranslation(["common", "admin"]);

    const navigate = useNavigate();
    const location = useLocation();
    const { id: buildingId } = location.state;

    const [isErrorMessageVisible, setIsErrorMessageVisible] = useState(false);

    const [buildingState, setBuildingState] =
        useState<IAdminPageBuildingDetail>(initialBuildingDetail);
    const [cachedBuilding, setCachedBuilding] =
        useState<IAdminPageBuildingDetail>(initialBuildingDetail);
    const [isBuildingChanged, setIsBuildingChanged] = useState(false);

    const [floors, setFloors] = useState<IFloor[]>([]);
    const floorColumns = useFloorColumns();

    const [locals, setLocals] = useState<ILocalWithFloorName[]>([]);
    const [cachedLocals, setCachedLocals] = useState<ILocalWithFloorName[]>([]);
    const localColumns = useLocalColumns();

    const [timeZones, setTimeZones] = useState<ITimeZoneInfo[]>([]);
    const timeZoneOptions = timeZones.map((timeZone, index) => ({ label: timeZone.displayName, id: timeZone.id }));
    const [selectedTimeZone, setSelectedTimeZone] = useState<{ label: string, id: string }>({ label: "", id: initialBuildingState.timezone });
    const [propertyGroups, setPropertyGroups] = useState<IPropertyGroup[]>([]);
    const [isFormSending, setIsFormSending] = useState(false);

    const onUpdateBuilding = async () => {
        setIsFormSending(true);

        const openingSchedule: ISchedule =
            convertScheduleToTicks(defaultMinTime);
        const closingSchedule: ISchedule =
            convertScheduleToTicks(defaultMaxTime);


        BuildingService.PutBuilding({
            ...buildingState,
            timezone: selectedTimeZone.id,
            openingSchedule,
            closingSchedule,
        })
            .then((response) => {

                if (response.status === SUCCESS_STATUS_CODE) {
                    navigate(0);
                    return;
                }
                setIsErrorMessageVisible(true);
            })
            .finally(() => setIsFormSending(false));
    };
    useEffect(() => {
        propertyGroupService.GetPropertyGroups().then(setPropertyGroups);
    }, [])
    useEffect(() => {
        BuildingService.GetBuildingById(buildingId).then((building) => {
            if (!building) return;

            Promise.all([BuildingService.GetTimeZones(), BuildingService.GetDefaultTimeZone()]).then(([timeZones, defaultTZ]) => {

                var initialTimeZone: string;
                var index: number = timeZones.findIndex((timeZone) => timeZone.id === building.timeZone)
                if (building.timeZone && building.timeZone.trim() && index >= 0) initialTimeZone = building.timeZone
                else {
                    index = timeZones.findIndex((timeZone) => timeZone.id === defaultTZ)
                    initialTimeZone = defaultTZ
                }

                setTimeZones(timeZones)
                setSelectedTimeZone({ label: timeZones[index].displayName, id: timeZones[index].id })

                const buildingState = {
                    id: building.id,
                    name: building.name,
                    address: building.address,
                    timeZone: initialTimeZone,
                    propertyGroupId: building.propertyGroupId,
                    propertyGroupName: building.propertyGroupName
                };
                setBuildingState(buildingState);
                setCachedBuilding(buildingState);
            })
        })

        FloorService.GetFloorsByBuildingId(buildingId).then((floors) => {
            setFloors(floors);
            profileService.GetProfilesByBuildingId(buildingId).then((profiles) => {
                localService.GetManagerLocals(buildingId).then((locals) => {
                    if (!locals) return;

                    const newLocals = locals.map((local) => {
                        const floor = floors.find(
                            (floor) => floor.id === local.floorId
                        );
                        const profile = profiles.find(
                            (profile) => profile.id === local.profileId
                        );

                        return { ...local, floorName: floor?.name, profileName: profile?.name ?? "", codes: local.codes ?? [] };
                    }).filter((local) => local.floorName)

                    setLocals(newLocals);
                    setCachedLocals(newLocals);
                });
            });
        });


    }, [buildingId]);

    useEffect(() => {
        setIsBuildingChanged(JSON.stringify(cachedBuilding) != JSON.stringify(buildingState) ||
            JSON.stringify(cachedLocals) != JSON.stringify(locals) ||
            JSON.stringify(selectedTimeZone.id) != JSON.stringify(buildingState.timeZone));
    }, [buildingState, locals, selectedTimeZone]);

    const updateBuildingState = <T extends keyof IEditBuildingRequest>(
        field: T,
        value: IEditBuildingRequest[T]
    ) => {
        setBuildingState((prevState) => ({
            ...prevState,
            [field]: value,
        }));
    };

    return (
        <>
            <IconButton onClick={() => navigate(-1)}>
                <ArrowBackIcon />
            </IconButton>

            <Box component={Paper} sx={{ width: "100%" }}>
                <form autoComplete="off" onSubmit={(event) => {
                    event.preventDefault();
                    if (JSON.stringify(cachedLocals) == JSON.stringify(locals)) onUpdateBuilding()
                }}>
                    <FormWrapper>
                        {isErrorMessageVisible && (
                            <Alert
                                severity="error"
                                sx={{ marginBottom: "1em" }}
                            >
                                {t("table.building.editErrorMessage")}
                            </Alert>
                        )}

                        <EntryWrapper>
                            <EntryTitle>
                                {t("table.building.name")} *
                            </EntryTitle>
                            <EntryField>
                                <TextField
                                    required={true}
                                    style={{ textAlign: "left", width: "100%" }}
                                    value={buildingState.name}
                                    onChange={(e) => {
                                        updateBuildingState(
                                            "name",
                                            e.target.value
                                        );
                                    }}
                                />
                            </EntryField>
                        </EntryWrapper>

                        <EntryWrapper>
                            <EntryTitle>
                                {t("table.building.address")} *
                            </EntryTitle>
                            <EntryField>
                                <TextField
                                    style={{ textAlign: "left", width: "100%" }}
                                    value={buildingState.address}
                                    onChange={(e) => {
                                        updateBuildingState(
                                            "address",
                                            e.target.value
                                        );
                                    }}
                                />
                            </EntryField>
                        </EntryWrapper>

                        <EntryWrapper>
                            <EntryTitle>{t("table.building.timezone")} * </EntryTitle>
                            <EntryField>

                                <Autocomplete
                                    value={selectedTimeZone}
                                    onChange={(e, newValue) => setSelectedTimeZone(newValue || { label: "", id: "" })}
                                    disablePortal
                                    sx={{ width: "100%", maxWidth: "100%", height: "2em" }}
                                    options={timeZoneOptions}
                                    isOptionEqualToValue={(option, value) => option.id === value.id || option.label === value.label}
                                    renderInput={(params) => (
                                        <TextField
                                            required={true}
                                            {...params}
                                            InputLabelProps={{
                                                children: null,
                                            }}
                                            onKeyDown={(e) => { e.key === "Enter" && e.preventDefault(); }}
                                        />
                                    )}
                                />
                            </EntryField>
                        </EntryWrapper >

                        <EntryWrapper>
                            <EntryTitle>{t("table.property.propertyGroup")} *</EntryTitle>
                            <EntryField>
                                <Select
                                    notched
                                    size="small"
                                    fullWidth
                                    defaultValue={-1}
                                    value={buildingState.propertyGroupId}
                                    onChange={(e) => {
                                        updateBuildingState(
                                            "propertyGroupId",
                                            Number(e.target.value)
                                        );
                                    }}
                                    id="property-select"
                                >
                                    {propertyGroups.map((propertyGroup) => (
                                        <MenuItem
                                            key={`property_${propertyGroup.id}`}
                                            value={propertyGroup.id}
                                        >
                                            {propertyGroup.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </EntryField>
                        </EntryWrapper>

                        <EntryWrapper
                            sx={{
                                justifyContent: "flex-end",
                                marginBlock: "1em",
                            }}
                        >
                            {isBuildingChanged && (
                                <EntryWrapper
                                    sx={{ justifyContent: "flex-end" }}
                                >
                                    <Progress isLoading={isFormSending}>
                                        <EditOnly>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                color="secondary"
                                                disabled={
                                                    !buildingState.name || !buildingState.name.trim() ||
                                                    !buildingState.address || !buildingState.address.trim() ||
                                                    !selectedTimeZone.id || !selectedTimeZone.id.trim() ||
                                                    !buildingState.propertyGroupId
                                                }
                                                sx={{
                                                    marginLeft: "1em",
                                                }}
                                            >
                                                {t("table.building.edit")}
                                            </Button>
                                        </EditOnly>
                                    </Progress>
                                </EntryWrapper>
                            )}
                        </EntryWrapper >
                    </FormWrapper >
                </form >

            </Box >
            <Box component={Paper} sx={{ width: "100%", marginTop: 4 }}>
                <Box sx={{ padding: 2, fontWeight: "bold" }}>
                    {t("table.floor.title")}
                </Box>
                <DataTable
                    dense
                    disableFiltrer={true}
                    rows={floors}
                    columns={floorColumns}
                    rowsPerPageOptions={[ROWS_PER_PAGE]}
                />
            </Box>

            <Box component={Paper} sx={{ width: "100%", marginY: 4 }}>
                <Box sx={{ padding: 2, fontWeight: "bold" }}>
                    {t("table.local.title")}
                </Box>
                <DataTable
                    dense
                    disableFiltrer={true}
                    rows={locals}
                    columns={localColumns}
                    rowsPerPageOptions={[ROWS_PER_PAGE]}
                />
            </Box>
        </>
    );
};

export default BuildingEditPage;
